(() => {
  const { createApp } = Vue;

  const app = createApp({
    delimiters: ['${', '}'],
    data() {
      return {
        cart: {
          item_count: 0
        },
        isCartOpen: false,
        shippingProgressSettings: {},
        freeShippingProgress: {},
        rewardConfig: {
          enabled: false,
          threshold: 0,
          variantId: null,
          title: '',
          imageUrl: '',
          productTitle: '',
          messageBefore: '',
          messageAfter: '',
          ignoredProductIds: []
        },
        isRewardSyncing: false,
        multiTierConfig: {
          enabled: false,
          messageAfterAll: '',
          ignoredProductIds: [],
          freeShipping: {
            enabled: false,
            amount: 0,
            title: '',
            subtitle: '',
            reachedTitle: '',
            messageBefore: ''
          },
          giftOffer: {
            enabled: false,
            title: '',
            reachedTitle: '',
            messageBefore: '',
            threshold: 0,
            variantId: null
          }
        },
        isGiftSyncing: false
      }
    },

    computed: {
      cart_items() {
        return this.cart.items;
      },

      cart_empty() {
        return this.cart.item_count == 0;
      },

      cart_total_price() {
        return this.cart.total_price;
      },

      rewardProgress() {
        const cfg = this.rewardConfig;
        if (!cfg || !cfg.enabled || !cfg.threshold) {
          return {
            eligible: false,
            remainingAmount: '',
            progressPercent: 0
          };
        }

        const total = this.getRewardEligibilityTotal();
        const remaining = Math.max(0, cfg.threshold - total);
        const eligible = remaining === 0;
        const progressPercent = Math.min(
          100,
          Math.round((total / cfg.threshold) * 100)
        );

        return {
          eligible,
          remainingAmount: eligible ? '' : this.formatMoney(remaining),
          progressPercent
        };
      },

      rewardMessage() {
        const cfg = this.rewardConfig;
        if (!cfg || !cfg.enabled) return '';

        if (this.rewardProgress.eligible) {
          return cfg.messageAfter || '';
        }

        const template =
          cfg.messageBefore || '';
        return template.replace('{amount}', this.rewardProgress.remainingAmount);
      },

      rewardItemInCart() {
        return Boolean(this.findRewardItem(this.cart));
      },

      multiTierMaxThreshold() {
        const cfg = this.multiTierConfig;
        if (!cfg) return 0;
        const thresholds = [];
        if (cfg.freeShipping.enabled && cfg.freeShipping.amount > 0) thresholds.push(cfg.freeShipping.amount);
        if (cfg.giftOffer.enabled && cfg.giftOffer.threshold > 0) thresholds.push(cfg.giftOffer.threshold);
        return Math.max(0, ...thresholds);
      },

      multiTierEligibilityTotal() {
        return this.getMultiTierEligibilityTotal();
      },

      multiTierTiers() {
        const cfg = this.multiTierConfig;
        if (!cfg || !cfg.enabled) return [];
        const maxThreshold = this.multiTierMaxThreshold;
        if (maxThreshold <= 0) return [];

        const total = this.multiTierEligibilityTotal;
        const tiers = [];

        if (cfg.freeShipping.enabled && cfg.freeShipping.amount > 0) {
          tiers.push({
            title: cfg.freeShipping.title || 'Free Shipping',
            reachedTitle: cfg.freeShipping.reachedTitle || '',
            subtitle: (cfg.freeShipping.subtitle || 'Spend {amount}').replace('{amount}', this.formatMoney(cfg.freeShipping.amount)),
            threshold: cfg.freeShipping.amount,
            position: (cfg.freeShipping.amount / maxThreshold) * 100,
            reached: total >= cfg.freeShipping.amount
          });
        }

        if (cfg.giftOffer.enabled && cfg.giftOffer.threshold > 0) {
          tiers.push({
            title: cfg.giftOffer.title || 'Free Gift',
            reachedTitle: cfg.giftOffer.reachedTitle || '',
            subtitle: 'Spend ' + this.formatMoney(cfg.giftOffer.threshold),
            threshold: cfg.giftOffer.threshold,
            position: (cfg.giftOffer.threshold / maxThreshold) * 100,
            reached: total >= cfg.giftOffer.threshold
          });
        }

        tiers.sort((a, b) => a.threshold - b.threshold);

        let prevThreshold = 0;
        for (const tier of tiers) {
          const segmentStart = prevThreshold;
          const segmentRange = tier.threshold - segmentStart;
          tier.segmentWidth = segmentRange > 0 ? (segmentRange / maxThreshold) * 100 : 0;

          if (segmentRange <= 0 || total >= tier.threshold) {
            tier.fillPercent = total >= tier.threshold ? 100 : 0;
          } else if (total <= segmentStart) {
            tier.fillPercent = 0;
          } else {
            tier.fillPercent = ((total - segmentStart) / segmentRange) * 100;
          }
          prevThreshold = tier.threshold;
        }

        return tiers;
      },

      multiTierAllReached() {
        const tiers = this.multiTierTiers;
        if (tiers.length === 0) return false;
        return tiers.every((t) => t.reached);
      },

      multiTierMessage() {
        const cfg = this.multiTierConfig;
        if (!cfg || !cfg.enabled) return '';
        if (this.multiTierAllReached) return cfg.messageAfterAll || '';

        const total = this.multiTierEligibilityTotal;
        const nextTier = this.multiTierTiers.find((t) => !t.reached);
        if (!nextTier) return cfg.messageAfterAll || '';

        let template = '';
        if (cfg.freeShipping.enabled && nextTier.threshold === cfg.freeShipping.amount) {
          template = cfg.freeShipping.messageBefore || '{amount} away from free shipping';
        } else if (cfg.giftOffer.enabled && nextTier.threshold === cfg.giftOffer.threshold) {
          template = cfg.giftOffer.messageBefore || '{amount} away from a free gift';
        }
        return template.replace('{amount}', this.formatMoney(nextTier.threshold - total));
      }
    },

    watch: {
      cart_total_price() {
        this.checkShippingProgress();
        this.syncRewardItem();
        this.syncGiftItem();
      },

      isCartOpen() {
        this.updateViews();
      },

      cart() {
        setTimeout(this.updateCartTotals, 0);
      }
    },

    mounted() {
      let vm = this;
      vm.setCartMainLoading(true);
      vm.getCartJS()
        .then((cart) => {
          vm.cart = cart;
          return vm.syncRewardItem();
        })
        .then(() => {
          return vm.syncGiftItem();
        })
        .then(() => {
          if (!vm.cart_empty) {
            document.getElementById("devHideExpressbtn").classList.remove("hide_Express_Buttons");
          }
        })
        .catch(console.error)
        .finally(() => {
          vm.setCartMainLoading(false);
        });

      vm.shippingProgressSettings = vm.getShippingProgressSettings();
      vm.rewardConfig = vm.getRewardConfig();
      vm.multiTierConfig = vm.getMultiTierConfig();
      vm.updateViews();
      vm.initDOMevents();
    },

    methods: {
      getCartJS() {
        return Shopify.theme.utils.getCartJS();
      },

      setCartMainLoading(isLoading) {
        const cartContainer = document.querySelector('#cart-drawer .cart-container');
        if (!cartContainer) {
          return
        };
        cartContainer.classList.toggle('loading', Boolean(isLoading));
      },

      showCart() {
        this.isCartOpen = true;
      },

      hideCart() {
        this.isCartOpen = false;
        let isCartPage = document.querySelectorAll('.template-cart').length;
        if (isCartPage ) {
          window.location.reload();
        }
      },

      updateViews() {
        let vm = this,
          isCartOpen = vm.isCartOpen;

        document.querySelector('html').classList[(isCartOpen) ? 'add' : 'remove']('cart-is-open');
        vm.$el.querySelector('.cart-overlay').classList[(isCartOpen) ? 'add' : 'remove']('visible');
        vm.$el.querySelector('.cart-main').classList[(isCartOpen) ? 'add' : 'remove']('visible');

      },

      updateCart() { },

      updateCartTotals() {
        // let cart = this.cart;
        fetch('/cart?view=total')
        .then(res => res.text())
        .then((res) => {
          let $res = Shopify.theme.utils.stringToHTML(res);
        // update cart total price
          let total_price = $res.querySelector('.cart-total-price').innerText;
        document.querySelectorAll('#cart-drawer .cart-total-price').forEach(($total) => {
          $total.innerText = total_price;
        });

        // update cart discount
          let total_discount = $res.querySelector('.cart-discount').innerText;
        document.querySelectorAll('#cart-drawer .cart-discount').forEach(($discount) => {
          $discount.innerText = total_discount;
        });

        // update cart counts
          let item_count = $res.querySelector('.total_items').innerText;
        document.querySelectorAll('.cart-item-count').forEach(($count) => {
          $count.innerText = item_count;
        });
        })
      },

      updateCartItemsView(openCart) {
        let vm = this;
        fetch('/cart?view=raw-drawer')
          .then(res => res.text())
          .then((res) => {
            vm.updateCartTotals();
            let $res = Shopify.theme.utils.stringToHTML(res);
            document.querySelector('#cart-drawer .cart-items').innerHTML = $res.querySelector('.cart-items').innerHTML;
            vm.initQuantityEvents();

            if (openCart) {
              vm.showCart();
            }
          })
          .finally(() => {
            vm.setCartMainLoading(false);
          })
          .catch(console.error);
      },

      updateItemQty(e, qty) {
        let vm = this;
        vm.setCartMainLoading(true);
        let $target = e.currentTarget;
        let $cartItem = $target.closest('.cart--item');
        let $qtyInput = $cartItem.querySelector('input[data-quantity]');
        let key = $qtyInput.dataset.key;

        $qtyInput.value = qty;

        Shopify.theme.utils.updateCartItemQuantity(key, qty)
          .then(res => res.json())
          .then((cart) => {
            vm.cart = cart;

            // let isCartPage = document.querySelectorAll('.template-cart').length;
            // if (isCartPage) {
            //   window.location.reload();

            // }

            // qty update success
            let cart_item = cart.items[$qtyInput.dataset.index];
            let $cartItem = $qtyInput.closest('.cart--item');
            // update item price

              if (!cart_item) {
                var arr = document.querySelectorAll('.cart--item');
                for(var i=0;i<arr.length;i++){
                  let inpt = arr[i].querySelector('input[data-quantity]');
                  if (inpt) {
                    inpt.setAttribute('data-index',i);
                  }
                }
              }

              cart_item = cart.items[$qtyInput.dataset.index];
              $cartItem = $qtyInput.closest('.cart--item');

            let item_final_price = cart_item ? cart_item.final_line_price : 0;
            let $item_finalprice = $cartItem.querySelector('.item-final-price');
            if ($item_finalprice) {
              $item_finalprice.innerText = window.cartStrings.currency_symbol == '€' ? Shopify.formatMoney( Math.ceil(item_final_price/ 100.00) * 100,`${window.cartStrings.currency_symbol}{{amount}}`).replace('.','#').replace(',','.').replace('#',','):Shopify.formatMoney(item_final_price,`${window.cartStrings.currency_symbol}{{amount}}`);

            }

            // update original price
            let $item_original_price = $cartItem.querySelector('.item-original-price');
            if ($item_original_price) {
              let item_original_price = cart_item ? (parseFloat($item_original_price.dataset.originalPrice) * cart_item.quantity) : 0;
              $item_original_price.innerText = window.cartStrings.currency_symbol == '€' ? Shopify.formatMoney( Math.ceil(item_original_price / 100.00)* 100,`${window.cartStrings.currency_symbol}{{amount}}`).replace('.','#').replace(',','.').replace('#',','):Shopify.formatMoney( item_original_price,`${window.cartStrings.currency_symbol}{{amount}}`);
            }

            // if item removed
            if (qty == 0) {
              $cartItem.classList.add('removed');
              $cartItem.remove();
            }

            vm.updateCartTotals(cart);

            // Dispatch cart:update event
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { openDrawer: false } }));


            // if cart empty, update items list
            if (vm.cart_empty) {
              vm.updateCartItemsView();
              document.getElementById("devHideExpressbtn").classList.add("hide_Express_Buttons");
            }

          })
          .finally(() => {
            vm.setCartMainLoading(false);
          })
          .catch(console.error);
      },

      updateItemQtyStep(e, qtyIncrBy) {
        let vm = this;
        let $target = e.currentTarget;
        let $qtyInput = $target.closest('.item-qty-wrap').querySelector('input[data-quantity]');
        let qty = parseInt($qtyInput.value) + qtyIncrBy;

        vm.updateItemQty(e, qty);

      },

      initDOMevents() {
        vm = this;
        document.addEventListener('cart:open', this.showCart);
        document.addEventListener('cart:close', this.hideCart);
        document.addEventListener('cart:update', (e) => {
          let openDrawer = e.detail.openDrawer;
          vm.getCartJS()
            .then((cart) => {
              vm.cart = cart;
              vm.updateCartTotals();
              vm.updateCartItemsView(openDrawer);
            })
            .catch(console.error);
        });

        document.addEventListener('product:added', (e) => {
          vm.showCart();
          vm.getCartJS()
            .then((cart) => {
              vm.cart = cart;
              vm.updateCartTotals();
              vm.initQuantityEvents();
              document.getElementById("devHideExpressbtn").classList.remove("hide_Express_Buttons");
            })
            .catch(console.error);
        });

        vm.initQuantityEvents();
      },

      initQuantityEvents() {
        // qty-minus
        document.querySelectorAll('.item-qty-wrap .item-qty-minus').forEach((item) => {
          item.removeEventListener('click', () => { });
          item.addEventListener('click', (e) => {
            vm.updateItemQtyStep(e, -1);
          });
        });

        // qty-plus
        document.querySelectorAll('.item-qty-wrap .item-qty-plus').forEach((item) => {
          item.removeEventListener('click', () => { });
          item.addEventListener('click', (e) => {
            vm.updateItemQtyStep(e, 1);
          });
        });

        // qty-change
        document.querySelectorAll('.item-qty-wrap [data-quantity]').forEach((item) => {
          item.removeEventListener('change', () => { });
          item.addEventListener('change', (e) => {
            let qty = item.value;
            //vm.updateItemQty(e, qty);

            if(qty === "" || qty.toLowerCase() === "none" || qty === 0){
              vm.updateItemQty(e, 0);
            }
            else {
              vm.updateItemQty(e, qty);
            }

          });
          item.closest('form').addEventListener('submit', (e) => {
            e.preventDefault();
        });
        });

        // item-remove
        document.querySelectorAll('.cart-item-remove').forEach((item) => {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            vm.updateItemQty(e, 0);
          });
        });
      },

      getShippingProgressSettings() {
        return Shopify.theme.utils.getShippingProgressSettings();
      },

      checkShippingProgress() {
        let vm = this;
        Shopify.theme.utils.getFreeShippingProgress({ ...vm.shippingProgressSettings }, { ...vm.cart })
          .then((progress) => {
            vm.freeShippingProgress = progress;
          })
          .catch(console.error);
      },

      submitBackinStockForm(e) {
        console.log(e);
        Shopify.theme.utils.submitBackinStockForm(e);
      },

      getRewardConfig() {
        const el = document.getElementById('cart-rewards-config');
        if (!el) return {
          enabled: false,
          threshold: 0,
          variantId: null,
          title: '',
          imageUrl: '',
          messageBefore: '',
          messageAfter: '',
          productTitle: '',
          ignoredProductIds: []
        };

        const rawIgnored = el.dataset.ignoredProductIds || '';
        const ignoredProductIds = rawIgnored
          ? rawIgnored.split(',').map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id))
          : [];

        return {
          enabled: el.dataset.rewardsEnabled === 'true',
          threshold: parseInt(el.dataset.rewardsThreshold || '0', 10),
          variantId: parseInt(el.dataset.rewardVariantId || '0', 10),
          title: el.dataset.rewardsTitle || '',
          imageUrl: el.dataset.rewardImageUrl || '',
          messageBefore: el.dataset.rewardsBefore || '',
          messageAfter: el.dataset.rewardsAfter || '',
          productTitle: el.dataset.rewardProductTitle || '',
          ignoredProductIds
        };
      },

      formatMoney(amount) {
        const currency = window.cartStrings?.currency_symbol || '$';
        if (currency === '€') {
          return Shopify.formatMoney(
            Math.ceil(amount / 100.0) * 100,
            `${currency}{{amount}}`
          )
            .replace('.', '#')
            .replace(',', '.')
            .replace('#', ',');
        }

        return Shopify.formatMoney(amount, `${currency}{{amount}}`);
      },

      findRewardItem(cart) {
        if (!cart || !cart.items) return null;
        const variantId = this.rewardConfig.variantId;
        return cart.items.find((item) => {
          if (!variantId) {
            return false;
          }
          return item.properties?._reward === 'true' && (item.variant_id === variantId || item.id === variantId);
        });
      },

      findStaleRewardItems(cart) {
        if (!cart || !cart.items) return [];
        const currentVariantId = this.rewardConfig.variantId;
        return cart.items.filter((item) => {
          if (item.properties?._reward !== 'true') {
            return false;
          }
          // If no current reward configured, all reward items are stale
          if (!currentVariantId) {
            return true;
          }
          // If variant doesn't match current config, it's stale
          return item.variant_id !== currentVariantId && item.id !== currentVariantId;
        });
      },

      async addRewardItem() {
        const variantId = this.rewardConfig.variantId;
        if (!variantId) {
          return Promise.resolve();
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { _reward: 'true' }
              }
            ]
          })
        });
        const data = await response.json();
        if (response.ok) {
          return data;
        }
        throw new Error(data.description || data.message || 'Failed to add reward item');
      },

      async removeRewardItem(itemKey) {
        if (!itemKey) {
          return Promise.resolve();
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        });
        const data = await response.json();
        if (response.ok) {
          return data;
        }
        throw new Error(data.description || data.message || 'Failed to remove reward item');
      },

      async syncRewardItem() {
        if (this.isRewardSyncing) {
          return;
        }

        this.isRewardSyncing = true;

        try {
          // First, clean up any stale reward items (from old reward configs)
          const staleItems = this.findStaleRewardItems(this.cart);
          if (staleItems.length > 0) {
            for (const item of staleItems) {
              await this.removeRewardItem(item.key);
            }
            // Refresh cart after removing stale items
            this.cart = await this.getCartJS();
            document.dispatchEvent(
              new CustomEvent('cart:update', { detail: { openDrawer: false } })
            );
          }

          // Now handle current reward config
          this.rewardConfig = this.getRewardConfig();
          const cfg = this.rewardConfig;
          if (!cfg || !cfg.enabled || !cfg.variantId || !cfg.threshold) {
            return;
          }

          const rewardItem = this.findRewardItem(this.cart);
          const cartTotal = this.getRewardEligibilityTotal();

          // Add reward if threshold reached and not in cart
          if (cartTotal >= cfg.threshold && !rewardItem) {
            await this.addRewardItem();
            this.cart = await this.getCartJS();
            document.dispatchEvent(
              new CustomEvent('cart:update', { detail: { openDrawer: false } })
            );
            return;
          }

          // Remove reward if below threshold and in cart
          if (cartTotal < cfg.threshold && rewardItem) {
            await this.removeRewardItem(rewardItem.key);
            this.cart = await this.getCartJS();
            document.dispatchEvent(
              new CustomEvent('cart:update', { detail: { openDrawer: false } })
            );
            return;
          }
        } catch (err) {
          console.error('Failed to sync reward item:', err);
        } finally {
          this.isRewardSyncing = false;
        }
      },

      getRewardEligibilityTotal() {
        const ignoredIds = this.rewardConfig.ignoredProductIds || [];
        const items = this.cart?.items || [];

        let total = 0;
        for (const item of items) {
          if (item.properties?._reward === 'true' || item.properties?._gift === 'true') {
            continue;
          }
          if (ignoredIds.length > 0 && item.product_id != null && ignoredIds.includes(item.product_id)) {
            continue;
          }
          total += item.final_line_price || 0;
        }
        return total;
      },

      getMultiTierConfig() {
        const el = document.getElementById('cart-drawer-multi-tier-config');
        if (!el) return {
          enabled: false,
          messageAfterAll: '',
          ignoredProductIds: [],
          freeShipping: { enabled: false, amount: 0, title: '', subtitle: '', reachedTitle: '', messageBefore: '' },
          giftOffer: { enabled: false, title: '', reachedTitle: '', messageBefore: '', threshold: 0, variantId: null }
        };

        const rawIgnored = el.dataset.multiTierIgnoredProductIds || '';
        const ignoredProductIds = rawIgnored
          ? rawIgnored.split(',').map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id))
          : [];

        return {
          enabled: true,
          messageAfterAll: el.dataset.multiTierMessageAfterAllThresholdsReached || '',
          ignoredProductIds,
          freeShipping: {
            enabled: el.dataset.freeShippingEnabled === 'true',
            amount: parseInt(el.dataset.freeShippingAmount || '0', 10),
            title: el.dataset.freeShippingTitle || '',
            subtitle: el.dataset.freeShippingSubtitle || '',
            reachedTitle: el.dataset.freeShippingReachedTitle || '',
            messageBefore: el.dataset.freeShippingMessageBeforeThresholdReached || ''
          },
          giftOffer: {
            enabled: el.dataset.giftOfferEnabled === 'true',
            title: el.dataset.giftOfferTitle || '',
            reachedTitle: el.dataset.giftOfferReachedTitle || '',
            messageBefore: el.dataset.giftOfferMessageBeforeThresholdReached || '',
            threshold: parseInt(el.dataset.giftOfferThreshold || '0', 10),
            variantId: parseInt(el.dataset.giftOfferVariantId || '0', 10) || null
          }
        };
      },

      getMultiTierEligibilityTotal() {
        const ignoredIds = this.multiTierConfig?.ignoredProductIds || [];
        const items = this.cart?.items || [];

        let total = 0;
        for (const item of items) {
          if (item.properties?._reward === 'true' || item.properties?._gift === 'true') continue;
          if (ignoredIds.length > 0 && item.product_id != null && ignoredIds.includes(item.product_id)) continue;
          total += item.final_line_price || 0;
        }
        return total;
      },

      findGiftItem(cart) {
        if (!cart || !cart.items) return null;
        const variantId = this.multiTierConfig?.giftOffer?.variantId;
        return cart.items.find((item) => {
          if (!variantId) return false;
          return item.properties?._gift === 'true' && (item.variant_id === variantId || item.id === variantId);
        });
      },

      findStaleGiftItems(cart) {
        if (!cart || !cart.items) return [];
        const currentVariantId = this.multiTierConfig?.giftOffer?.variantId;
        return cart.items.filter((item) => {
          if (item.properties?._gift !== 'true') return false;
          if (!currentVariantId) return true;
          return item.variant_id !== currentVariantId && item.id !== currentVariantId;
        });
      },

      async addGiftItem() {
        const variantId = this.multiTierConfig?.giftOffer?.variantId;
        if (!variantId) return;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }]
          })
        });
        const data = await response.json();
        if (response.ok) return data;
        throw new Error(data.description || data.message || 'Failed to add gift item');
      },

      async removeGiftItem(itemKey) {
        if (!itemKey) return;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: itemKey, quantity: 0 })
        });
        const data = await response.json();
        if (response.ok) return data;
        throw new Error(data.description || data.message || 'Failed to remove gift item');
      },

      async syncGiftItem() {
        if (this.isGiftSyncing) return;
        this.isGiftSyncing = true;

        try {
          const staleItems = this.findStaleGiftItems(this.cart);
          if (staleItems.length > 0) {
            for (const item of staleItems) {
              await this.removeGiftItem(item.key);
            }
            this.cart = await this.getCartJS();
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { openDrawer: false } }));
          }

          const cfg = this.multiTierConfig;
          if (!cfg?.enabled || !cfg.giftOffer?.enabled || !cfg.giftOffer?.variantId || !cfg.giftOffer?.threshold) return;

          const giftItem = this.findGiftItem(this.cart);
          const cartTotal = this.getMultiTierEligibilityTotal();

          if (cartTotal >= cfg.giftOffer.threshold && !giftItem) {
            await this.addGiftItem();
            this.cart = await this.getCartJS();
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { openDrawer: false } }));
            return;
          }

          if (cartTotal < cfg.giftOffer.threshold && giftItem) {
            await this.removeGiftItem(giftItem.key);
            this.cart = await this.getCartJS();
            document.dispatchEvent(new CustomEvent('cart:update', { detail: { openDrawer: false } }));
          }
        } catch (err) {
          console.error('Failed to sync gift item:', err);
        } finally {
          this.isGiftSyncing = false;
        }
      }

    }
  }).mount('#cart-drawer');

  // Asigna la instancia del componente a una variable global
  window.cartApp = app;
})();
